import { useEffect, useRef, useState } from 'react';
import { getTotpStatus, listAuthMethods } from '../../api/account';
import { authorizeExportDownload, beginExportTotpStepUp, createExport, downloadExport, getExport, listExports, listUnlockMethods, type DataExport, type UnlockMethodSummary } from '../../api/privacy';
import { apiErrorMessage } from '../../api/errorMessage';
import { useAuth } from '../../auth/useAuth';
import DangerZone from '../../components/DangerZone';
import { formatDeletionDate } from '../../components/deletionDate';

type UnlockState =
  | { status: 'loading' }
  | { status: 'loaded'; methods: UnlockMethodSummary[] }
  | { status: 'error' };

export default function PrivacyPage() {
  const { user } = useAuth();
  const [unlockState, setUnlockState] = useState<UnlockState>({ status: 'loading' });
  const [exports, setExports] = useState<DataExport[]>([]);
  const [exportError, setExportError] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [passwordAvailable, setPasswordAvailable] = useState(false);
  const [stepUpMethod, setStepUpMethod] = useState<'password' | 'totp'>('password');
  const [submitting, setSubmitting] = useState(false);
  const generation = useRef(0);

  useEffect(() => {
    let active = true;
    listUnlockMethods().then(
      (methods) => {
        if (active) setUnlockState({ status: 'loaded', methods });
      },
      () => {
        if (active) setUnlockState({ status: 'error' });
      },
    );
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    const run = ++generation.current;
    Promise.all([listExports(), getTotpStatus(), listAuthMethods()]).then(([rows, totp, methods]) => {
      if (run === generation.current) { const hasPassword = methods.some((item) => item.source === 'meridian' && item.linked); setExports(rows); setTotpEnabled(totp.enabled); setPasswordAvailable(hasPassword); setStepUpMethod(hasPassword ? 'password' : totp.enabled ? 'totp' : 'password'); }
    }, (error) => { if (run === generation.current) setExportError(apiErrorMessage(error, 'Export status is unavailable.')); });
    return () => { generation.current += 1; };
  }, []);
  useEffect(() => {
    const active = exports.find((item) => item.status === 'queued' || item.status === 'building');
    if (!active) return;
    const run = generation.current;
    const timer = window.setTimeout(() => { void getExport(active.id).then((next) => {
      if (run === generation.current) setExports((items) => [next, ...items.filter((item) => item.id !== next.id)]);
    }, () => { if (run === generation.current) setExportError('Could not refresh export status.'); }); }, 2000);
    return () => window.clearTimeout(timer);
  }, [exports]);

  async function startExport() {
    setSubmitting(true); setExportError('');
    try {
      let input: { password?: string; challenge?: string; totp_code?: string };
      if (stepUpMethod === 'totp') { const step = await beginExportTotpStepUp(); input = { challenge: step.challenge, totp_code: totpCode }; }
      else input = { password };
      const row = await createExport(input); setExports((items) => [row, ...items]); setPassword(''); setTotpCode('');
    } catch (error) { setExportError(apiErrorMessage(error, 'Could not start the export.')); }
    finally { setSubmitting(false); }
  }

  async function saveExport(row: DataExport) {
    try {
      const current = await authorizeExportDownload(row.id);
      if (!current.download_token) throw new Error('Download authorization is unavailable.');
      const blob = await downloadExport(row.id, current.download_token); const url = URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url; link.download = `meridian-export-${row.id}.zip`; link.click(); URL.revokeObjectURL(url);
    } catch (error) { setExportError(apiErrorMessage(error, 'Could not download the export.')); }
  }

  if (!user) return null;

  return (
    <div className="account-page-content">
      <div className="page-heading">
        <p className="eyebrow">Control</p>
        <h1>Data &amp; privacy</h1>
        <p>See what Meridian keeps, how it is protected, and what happens if you leave.</p>
      </div>

      <section className="card">
        <div className="section-heading">
          <div>
            <h2>Your data</h2>
            <p>A plain-language inventory of data Meridian needs to operate your account.</p>
          </div>
          <span className="status-pill positive">Content encrypted</span>
        </div>
        <dl className="detail-list">
          <div>
            <dt>Workspace content</dt>
            <dd>End-to-end encrypted sync blobs. The server cannot read your messages, events, contacts, or activities.</dd>
          </div>
          <div>
            <dt>Account details</dt>
            <dd>Your email, profile, plan, device registrations, and connection health metadata.</dd>
          </div>
          <div>
            <dt>Connected services</dt>
            <dd>Provider authorization is stored server-side and is removed when the account is purged.</dd>
          </div>
          <div>
            <dt>Local data</dt>
            <dd>Decrypted workspace data remains in Meridian on each device until the app receives the deletion signal or is removed locally.</dd>
          </div>
        </dl>
      </section>

      <section className="card" aria-labelledby="unlock-methods-heading">
        <div className="section-heading">
          <div>
            <h2 id="unlock-methods-heading">Encryption &amp; unlock methods</h2>
            <p>Only method names and dates are shown here. Secrets, plaintext keys, and wrapped-key blobs never enter this page.</p>
          </div>
        </div>
        <UnlockMethods state={unlockState} />
      </section>

      <section className="card">
        <div className="section-heading">
          <div>
            <h2>Export</h2>
            <p>Take a copy before deleting your account.</p>
          </div>
          <span className="status-pill neutral">Beta · encrypted archive</span>
        </div>
        <p className="supporting-copy">
          The signed archive contains verified encrypted sync records and wrapped keys. By default, the offline decryptor reconstructs the stable current state of supported entity and dataset families in a fresh CRR database. Use <code>--raw-change-log</code> only for diagnostic change-log output. Archive format v1 cannot independently prove historical completeness; completeness currently depends on server pruning remaining disabled.
        </p>
        <p className="supporting-copy">
          <a href="https://github.com/SerenitySoftware/meridian/releases/latest/download/meridian-decrypt-macos-arm64.dmg">Download the decryptor for Apple Silicon macOS</a>{' '}
          (<a href="https://github.com/SerenitySoftware/meridian/releases/latest/download/meridian-decrypt-macos-arm64.dmg.sha256">SHA-256 sidecar</a>). Windows, Linux, and Intel Mac packages are not available yet.
        </p>
        <div className="form-actions privacy-actions">
          {totpEnabled && passwordAvailable && <fieldset><legend>Verify with</legend><label><input type="radio" name="export-step-up" checked={stepUpMethod === 'password'} onChange={() => setStepUpMethod('password')} /> Password</label><label><input type="radio" name="export-step-up" checked={stepUpMethod === 'totp'} onChange={() => setStepUpMethod('totp')} /> Authenticator</label></fieldset>}
          <label><span>{stepUpMethod === 'totp' ? 'Authenticator code' : 'Current password'}</span><input aria-label={stepUpMethod === 'totp' ? 'Authenticator code' : 'Current password'} type={stepUpMethod === 'totp' ? 'text' : 'password'} inputMode={stepUpMethod === 'totp' ? 'numeric' : undefined} autoComplete={stepUpMethod === 'totp' ? 'one-time-code' : 'current-password'} value={stepUpMethod === 'totp' ? totpCode : password} onChange={(event) => stepUpMethod === 'totp' ? setTotpCode(event.target.value) : setPassword(event.target.value)} /></label>
          <button className="btn btn-secondary" type="button" disabled={submitting || (stepUpMethod === 'totp' ? !totpCode.trim() : !password) || (!totpEnabled && !passwordAvailable)} onClick={() => void startExport()}>{submitting ? 'Queueing…' : 'Create encrypted export'}</button>
        </div>
        {exportError && <p className="error-message" role="alert">{exportError}</p>}
        <ul className="unlock-method-list" aria-label="Data exports">{exports.map((item) => <li key={item.id}><div><strong>{exportLabel(item.status)}</strong><span>Requested {formatDate(item.created_at)} · {item.changeset_count + item.dataset_count} encrypted records</span></div>{item.status === 'ready' ? <button className="btn btn-secondary btn-sm" type="button" onClick={() => void saveExport(item)}>Download</button> : <span className="status-pill neutral">{item.status}</span>}</li>)}</ul>
      </section>

      <section className="card">
        <div className="section-heading">
          <div>
            <h2>Notifications &amp; privacy defaults</h2>
            <p>What Meridian currently sends and collects.</p>
          </div>
        </div>
        <dl className="detail-list">
          <div>
            <dt>Security notices</dt>
            <dd><span className="status-pill positive">On</span> New-device, password-reset, and deletion notices protect the account and cannot be disabled.</dd>
          </div>
          <div>
            <dt>Sync wake-ups</dt>
            <dd>Silent device notifications contain no message or calendar content. Delivery is managed per device in Meridian.</dd>
          </div>
          <div>
            <dt>Marketing</dt>
            <dd><span className="status-pill neutral">None</span> Meridian does not currently send configurable marketing notifications.</dd>
          </div>
          <div>
            <dt>Product analytics</dt>
            <dd><span className="status-pill neutral">Off</span> The account center has no opt-in product analytics collection.</dd>
          </div>
        </dl>
      </section>

      {user.deleted_at && user.scheduled_purge_at ? (
        <DeletionProgress
          requestedAt={user.deleted_at}
          purgeAt={user.scheduled_purge_at}
        />
      ) : (
        <DangerZone />
      )}
    </div>
  );
}

function UnlockMethods({ state }: { state: UnlockState }) {
  if (state.status === 'loading') {
    return <p className="supporting-copy" role="status">Loading unlock-method metadata…</p>;
  }
  if (state.status === 'error') {
    return <p className="error-message" role="alert">Unlock-method metadata is unavailable. No key material was exposed.</p>;
  }
  if (state.methods.length === 0) {
    return (
      <div className="empty-state">
        <strong>No server-stored unlock methods</strong>
        <p>Finish encryption setup in the Meridian app before adding another device.</p>
      </div>
    );
  }
  return (
    <ul className="unlock-method-list" aria-label="Available unlock methods">
      {state.methods.map((method) => (
        <li key={method.kind}>
          <div>
            <strong>{unlockMethodLabel(method.kind)}</strong>
            <span>
              Envelope version {method.version} · added {formatDate(method.created_at)} · {method.last_used_at
                ? `last used ${formatDate(method.last_used_at)}`
                : 'not yet used to retrieve a wrap'}
            </span>
          </div>
          <span className="status-pill positive">Available</span>
        </li>
      ))}
    </ul>
  );
}

function DeletionProgress({ requestedAt, purgeAt }: { requestedAt: string; purgeAt: string }) {
  return (
    <section className="card danger-zone" aria-labelledby="deletion-progress-heading">
      <div className="section-heading">
        <div>
          <h2 id="deletion-progress-heading">Deletion in progress</h2>
          <p>You can cancel from the banner above until the scheduled purge begins.</p>
        </div>
        <span className="status-pill neutral">Grace period</span>
      </div>
      <ol className="deletion-progress">
        <li className="complete">
          <strong>Deletion requested</strong>
          <span>{formatDate(requestedAt)}</span>
        </li>
        <li className="current">
          <strong>Seven-day recovery window</strong>
          <span>Your server account is scheduled; use Cancel deletion above to restore it.</span>
        </li>
        <li>
          <strong>Permanent purge</strong>
          <span>{formatDeletionDate(purgeAt)} — encrypted sync data, connections, provider tokens, devices, profiles, and unlock wraps are removed.</span>
        </li>
      </ol>
      <p className="retention-note">
        Meridian retains a minimal deletion audit record for security and compliance. It does not contain your encrypted workspace content.
      </p>
    </section>
  );
}

function unlockMethodLabel(kind: string): string {
  switch (kind) {
    case 'recovery_code': return 'Recovery code';
    case 'passphrase': return 'Encryption passphrase';
    default: return 'Other client-managed method';
  }
}

function exportLabel(status: DataExport['status']): string {
  switch (status) {
    case 'queued': return 'Export queued';
    case 'building': return 'Building encrypted export';
    case 'ready': return 'Export ready';
    case 'failed': return 'Export failed';
    case 'expired': return 'Export expired';
  }
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'date unavailable';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
